/**
 * MultiSelect - Reusable multi-select dropdown component
 *
 * A dropdown that allows selecting multiple options from a list.
 * Uses shadcn Popover, Button, and Checkbox components.
 *
 * @example
 * ```tsx
 * <MultiSelect
 *   label="Regions"
 *   options={[{ value: 'hq', label: 'HQ' }, { value: 'ny', label: 'North York' }]}
 *   selected={['hq']}
 *   onChange={setSelected}
 * />
 * ```
 *
 * @see /src/features/gallery/components/filters/MultiSelectDropdown.tsx - Original implementation
 */

import React from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

export interface MultiSelectOption {
  value: string
  label: string
}

export interface MultiSelectProps {
  /** Label shown on the trigger button and in the dropdown header */
  label: string
  /** Available options to choose from */
  options: MultiSelectOption[]
  /** Currently selected option values */
  selected: string[]
  /** Callback when selection changes */
  onChange: (selected: string[]) => void
  /** Placeholder text when nothing is selected (default: "Any") */
  placeholder?: string
  /** Custom class for the trigger button */
  className?: string
  /** Width of the popover content (default: "w-60") */
  popoverWidth?: string
  /** Disable the entire component */
  disabled?: boolean
}

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Any',
  className,
  popoverWidth = 'w-60',
  disabled = false,
}: MultiSelectProps) {
  const toggle = (value: string) => {
    const set = new Set(selected)
    if (set.has(value)) {
      set.delete(value)
    } else {
      set.add(value)
    }
    onChange(Array.from(set))
  }

  const clear = () => onChange([])
  const count = selected.length

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('min-w-[9rem] justify-between', className)}
          disabled={disabled}
        >
          <span>{label}</span>
          <span className="text-xs text-muted-foreground">
            {count ? `${count} selected` : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn(popoverWidth, 'p-2')}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium">{label}</div>
          {count > 0 && (
            <button
              type="button"
              className="text-xs underline hover:text-primary"
              onClick={clear}
            >
              Clear
            </button>
          )}
        </div>
        <div className="max-h-64 overflow-auto space-y-1">
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5"
            >
              <Checkbox
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
              />
              <span className="truncate" title={opt.label}>
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default MultiSelect
